/**
 * Local hotseat game session. [P6-place-ux support, per lane brief "Hard rules"]
 *
 * All seats local, no socket: `createGame` + `applyAction` called directly against the shared engine.
 * This is the same call shape a future networked session will use (dispatch an action, receive
 * `{ state, events }`), so swapping in a WebSocket-backed session later only means replacing this file's
 * internals, not anything that depends on it. It also happens to be genuinely useful on its own — this
 * *is* hotseat play.
 */

import {
  applyAction,
  applyAll,
  buildTileIndex,
  createGame,
  legalMeepleTargets as engineLegalMeepleTargets,
  legalPlacements as engineLegalPlacements,
  type GameAction,
  type GameEvent,
  type GameState,
  type Result,
  type Rotation,
  type TileIndex,
} from '@carc/engine';
import type { RoomOptions } from '@carc/protocol';

export interface SeatSetup {
  readonly name: string;
  readonly colour: string;
}

export interface LocalSessionOptions {
  readonly seed: number;
  readonly seats: readonly SeatSetup[];
  readonly options: RoomOptions;
  /**
   * An action log to replay on construction — how a saved game comes back.
   *
   * The engine is deterministic, so the same seed and the same actions rebuild the same game, move
   * for move. A log that fails to replay is DISCARDED rather than half-applied: a partially replayed
   * game is a corrupt game that looks fine, which is far worse than starting over knowingly.
   */
  readonly replay?: readonly GameAction[];
}

export type SessionListener = (state: GameState, events: readonly GameEvent[]) => void;

/**
 * What the gameplay controllers actually need from a session.
 *
 * `PlacementController` and `MeeplePicker` are indifferent to whether the game is running locally or
 * against a server, so they take this rather than a concrete class. `LocalSession` and `RemoteSession`
 * both satisfy it structurally.
 */
export interface PlayableSession {
  readonly tiles: TileIndex;
  readonly state: GameState;
  legalPlacementsForCurrentTile(): { pos: { x: number; y: number }; rotation: Rotation }[];
  legalMeepleTargets(): number[];
  placeTile(
    pos: { x: number; y: number },
    rotation: Rotation,
  ): Result<{ state: GameState; events: readonly GameEvent[] }>;
  placeMeeple(featureIndex: number): Result<{ state: GameState; events: readonly GameEvent[] }>;
  skipMeeple(): Result<{ state: GameState; events: readonly GameEvent[] }>;
}

export class LocalSession {
  readonly tiles: TileIndex;
  readonly seats: readonly SeatSetup[];
  readonly actions: GameAction[] = [];

  private currentState: GameState;
  private readonly listeners = new Set<SessionListener>();

  constructor(opts: LocalSessionOptions) {
    this.tiles = buildTileIndex(opts.options.packs);
    this.seats = opts.seats;
    this.currentState = createGame({
      seed: opts.seed,
      seatCount: opts.seats.length,
      packs: opts.options.packs,
      stackCount: opts.options.stackCount,
    });

    if (opts.replay && opts.replay.length > 0) {
      const replayed = applyAll(this.currentState, opts.replay, this.tiles);
      if (replayed.ok) {
        this.currentState = replayed.value.state;
        this.actions.push(...opts.replay);
        // Kept, not discarded. These are every event the game has ever produced, regenerated exactly
        // by the same pure engine that produced them the first time — which is how a resumed game gets
        // its history back instead of opening on a blank log that reads as "the past was lost".
        this.replayedEvents = replayed.value.events;
      }
      // Not ok: keep the fresh game. `replayFailed` lets the caller say so out loud rather than
      // silently handing the player a brand-new board they did not ask for.
      this.replayFailed = !replayed.ok;
    }
  }

  /** True when a saved log could not be replayed and this is a fresh game instead. */
  readonly replayFailed: boolean = false;

  /**
   * The events a replay regenerated, in order — empty for a game that started fresh.
   *
   * They are deliberately NOT emitted to listeners: the replay happens in this constructor, before the
   * app has wired anything up, and firing a hundred moves through the animation director would make a
   * resumed game replay itself in front of the player. The caller decides what to do with them.
   */
  readonly replayedEvents: readonly GameEvent[] = [];

  get state(): GameState {
    return this.currentState;
  }

  onChange(listener: SessionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private commit(result: Result<{ state: GameState; events: readonly GameEvent[] }>, action: GameAction): Result<{ state: GameState; events: readonly GameEvent[] }> {
    if (!result.ok) return result;
    this.currentState = result.value.state;
    this.actions.push(action);
    for (const listener of this.listeners) listener(this.currentState, result.value.events);
    return result;
  }

  draw(stack?: number): Result<{ state: GameState; events: readonly GameEvent[] }> {
    const action: GameAction =
      stack === undefined
        ? { type: 'draw', seat: this.currentState.activeSeat }
        : { type: 'draw', seat: this.currentState.activeSeat, stack };
    return this.commit(applyAction(this.currentState, action, this.tiles), action);
  }

  placeTile(pos: { x: number; y: number }, rotation: Rotation): Result<{ state: GameState; events: readonly GameEvent[] }> {
    const action: GameAction = { type: 'place_tile', seat: this.currentState.activeSeat, pos, rotation };
    return this.commit(applyAction(this.currentState, action, this.tiles), action);
  }

  placeMeeple(featureIndex: number): Result<{ state: GameState; events: readonly GameEvent[] }> {
    const action: GameAction = { type: 'place_meeple', seat: this.currentState.activeSeat, featureIndex };
    return this.commit(applyAction(this.currentState, action, this.tiles), action);
  }

  skipMeeple(): Result<{ state: GameState; events: readonly GameEvent[] }> {
    const action: GameAction = { type: 'skip_meeple', seat: this.currentState.activeSeat };
    return this.commit(applyAction(this.currentState, action, this.tiles), action);
  }

  legalPlacementsForCurrentTile(): { pos: { x: number; y: number }; rotation: Rotation }[] {
    if (!this.currentState.currentTile) return [];
    return engineLegalPlacements(this.currentState, this.tiles, this.currentState.currentTile.defId);
  }

  legalMeepleTargets(): number[] {
    return engineLegalMeepleTargets(this.currentState, this.tiles);
  }

  seatFor(index: number): SeatSetup {
    return this.seats[index] ?? { name: `Seat ${index + 1}`, colour: 'slate' };
  }
}
